import { useMemo, useState } from "react";
import { FormAlert, FormSearchSelect } from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import { useAuth } from "../../auth/AuthProvider";
import { useEntityLabels } from "../../i18n/EntityLabelsProvider";
import { useCustomerScope } from "../financeStructure/useCustomerScope";
import {
  IncomeExpensesPanel,
  type CashLedgerSection,
} from "./IncomeExpensesPanel";
import "../financeStructure/financeStructure.css";
import { ScreenHeader } from "../../next/page/ScreenHeader";

export type IncomeExpensesPageProps = {
  section?: CashLedgerSection;
};

type CustomerOption = { id: string; name: string };

const SECTION_SUB: Record<CashLedgerSection, string> = {
  ledger: "רישום הכנסות והוצאות, קופות פנימיות וסיכומים",
  bank: "התאמת תנועות הבנק לתנועות במערכת",
  suppliers: "ספקים, ההוצאות שלהם והמסמכים",
  receipts: "קבלות שהופקו — ידנית או דרך EZcount",
  accounting: "מסמכי הנהלת חשבונות",
  payment_requests: "דרישות תשלום, אישור והמרה להוצאה",
  documents: "חוזים, אישורים ומסמכים כלליים",
};

export function IncomeExpensesPage({ section = "ledger" }: IncomeExpensesPageProps) {
  const labels = useEntityLabels();
  const auth = useAuth();
  const { customers, writeCustomerId, loading, error } = useCustomerScope();
  /** null = show all customers (default). */
  const [filterCustomerId, setFilterCustomerId] = useState<string | null>(null);

  const selected = useMemo(
    () =>
      filterCustomerId
        ? (customers.find((c) => c.id === filterCustomerId) ?? null)
        : null,
    [customers, filterCustomerId],
  );

  const customerIds = useMemo(() => customers.map((c) => c.id), [customers]);
  const customerNames = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c.name])),
    [customers],
  );

  const options = useMemo<CustomerOption[]>(
    () => [{ id: "", name: "הכל" }, ...customers.map((c) => ({ id: c.id, name: c.name }))],
    [customers],
  );

  const selectedOption = useMemo(
    () => options.find((o) => o.id === (filterCustomerId ?? "")) ?? options[0] ?? null,
    [options, filterCustomerId],
  );

  const enabledFeatures = auth.activeTenant?.enabledFeatures ?? [];
  const moduleOn =
    enabledFeatures.includes("finance.cash_ledger") || enabledFeatures.length === 0;

  return (
    <div className="page-section page-section--list">
      <ScreenHeader sub={SECTION_SUB[section]} />
      {!moduleOn ? (
        <FormAlert severity="error">מודול הכנסות והוצאות אינו מופעל לארגון זה.</FormAlert>
      ) : null}

      {loading ? <FormAlert severity="info">טוען…</FormAlert> : null}
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}

      {!loading && customers.length === 0 ? (
        <FormAlert severity="info">
          אין {labels.customers} פעילים — צרו {labels.customer} תחילה.
        </FormAlert>
      ) : null}

      {!loading && customers.length > 0 && moduleOn && writeCustomerId ? (
        <div className="cash-ledger-page">
          <div className="cash-ledger-page__scope">
            <FormSearchSelect
              label={labels.customer}
              options={options}
              value={selectedOption}
              onChange={(val: unknown) => {
                const opt = val as CustomerOption | null;
                setFilterCustomerId(opt?.id ? opt.id : null);
              }}
              getOptionLabel={(o) => (o as CustomerOption)?.name || ""}
              isOptionEqual={(a, b) =>
                (a as CustomerOption | null)?.id === (b as CustomerOption | null)?.id
              }
              placeholder={`סינון לפי ${labels.customer}…`}
            />
          </div>

          <IncomeExpensesPanel
            customerId={filterCustomerId}
            customerIds={customerIds}
            customerNames={customerNames}
            writeCustomerId={writeCustomerId}
              archived={
                selected?.status === "cancelled" || selected?.status === "archived"
              }
            enabledFeatures={
              enabledFeatures.length > 0
                ? enabledFeatures
                : [
                    "finance.cash_ledger",
                    "finance.suppliers",
                    "finance.payment_requests",
                    "finance.documents",
                    "finance.receipts",
                    "finance.bank_reconciliation",
                    "finance.accounting_documents",
                  ]
            }
            section={section}
          />
        </div>
      ) : null}
    </div>
  );
}
